import { inject, Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, from, Observable } from 'rxjs';
import { EventTimeline, MatrixClient, MatrixEvent, RoomStateEvent } from 'matrix-js-sdk';
import { catchError, map, mergeMap, retry } from 'rxjs/operators';
import { EventType, StateEvents } from 'matrix-js-sdk/lib/@types/event';
import { MatrixService } from '../matrix/matrix.service';

export interface StateEvent {
  roomId: string;
  eventType: keyof StateEvents;
  content: any;
  stateKey?: string;
}

export interface SentEventResult {
  event: StateEvent;
  success: boolean;
  value?: MatrixEvent;
  reason?: Error;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Service
 * - zum Senden mehrerer StateEvents
 * - und Warten, bis die Events im RoomState angekommen sind.
 * - Timeout pro Event unterstützt
 * - Retry bei fehlerhaften sendStateEvent
 * - mit sauberem Aufruf von removeListener, um Memory Leaks zu vermeiden
 * - liefert Observable
 * Begründung: technische Hilfsklasse, keine AFO
 */
export class StateEventService {
  private readonly matrixService = inject(MatrixService);

  /** Sendet mehrere StateEvents und wartet auf alle. */
  sendAndWaitAll$(
    client: MatrixClient,
    events: StateEvent[],
    retryCount = 2,
    timeoutMs = 5000
  ): Observable<SentEventResult[]> {
    const observables = events.map(e => this.sendAndWaitEvent(client, e, retryCount, timeoutMs));
    return forkJoin(observables);
  }

  /** Async-Interface für vorherige Methode. */
  async sendAndWaitAll(
    client: MatrixClient,
    events: StateEvent[],
    retryCount = 2,
    timeoutMs = 5000
  ): Promise<SentEventResult[]> {
    return await firstValueFrom(this.sendAndWaitAll$(client, events, retryCount, timeoutMs));
  }

  async sendStateEventAvatar(roomId: string, avatarUrl: string): Promise<void> {
    await this.matrixService.client.sendStateEvent(roomId, EventType.RoomAvatar, {
      url: avatarUrl
    });
  }

  /** Wartet, bis ein StateEvent im Raum angekommen ist. */
  private waitForStateEvent(client: MatrixClient, event: StateEvent, timeoutMs = 5000): Observable<MatrixEvent> {
    return new Observable<MatrixEvent>(subscriber => {
      const { roomId, eventType, stateKey = '' } = event;
      const room = client.getRoom(roomId);

      if (!room) {
        subscriber.error(new Error(`Room for ${roomId} not found`));
        return;
      }

      // RoomState gemäß verwendeter Matrix-Version holen
      const timeline = room.getLiveTimeline();
      const roomState = timeline.getState(EventTimeline.FORWARDS);

      if (!roomState) {
        subscriber.error(new Error(`RoomState for ${roomId} not found`));
        return;
      }

      // Prüfen, ob Event bereits vorhanden
      const existing = roomState?.getStateEvents(eventType, stateKey);
      if (existing) {
        subscriber.next(existing);
        subscriber.complete();
        return;
      }

      // Listener für neue State-Events registrieren
      const handler = (matrixEvent: MatrixEvent): void => {
        if (
          matrixEvent.getRoomId() === roomId &&
          matrixEvent.getType() === eventType &&
          matrixEvent.getStateKey() === stateKey
        ) {
          cleanup();
          subscriber.next(matrixEvent);
          subscriber.complete();
        }
      };
      roomState.on(RoomStateEvent.Events, handler);

      // Timeout
      const timeoutId = setTimeout(() => {
        cleanup();
        subscriber.error(new Error('Timeout waiting for state event'));
      }, timeoutMs);

      const cleanup = (): void => {
        clearTimeout(timeoutId);
        roomState.removeListener(RoomStateEvent.Events, handler);
      };

      // Cleanup auch, wenn unsubscribed wird.
      return cleanup;
    });
  }

  /** Sendet ein einzelnes StateEvent und wartet auf dessen Eintreffen. */
  private sendAndWaitEvent(
    client: MatrixClient,
    event: StateEvent,
    retryCount = 2,
    timeoutMs = 5000
  ): Observable<SentEventResult> {
    return from(client.sendStateEvent(event.roomId, event.eventType, event.content, event.stateKey)).pipe(
      retry(retryCount), // bei Fehler erneut versuchen
      mergeMap(() =>
        this.waitForStateEvent(client, event, timeoutMs).pipe(
          map(matrixEvent => ({ event, success: true, value: matrixEvent })),
          catchError(reason =>
            // falls Timeout oder anderes Problem beim Eintreffen
            from([{ event, success: false, reason }])
          )
        )
      ),
      catchError(reason => from([{ event, success: false, reason }]))
    );
  }
}
